import { useGeneralStore } from "./store/general/general.store";
import i18n from "./i18n";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "react-oidc-context";
import { Routes } from "./router/routes";
import { useAuthStore } from "./store/auth/auth.store";

/**
 * App component
 *
 * This component is the root component of the application.
 * It is used to handle the authentication state of the application.
 * It also handles the locale change.
 */
export default function App() {
    const auth = useAuth();
    const locale = useGeneralStore((state) => state.currentLocale);
    const location = useLocation();

    /**
     * Change the i18n language when the locale is changed by the user
     */
    useEffect(() => {
        void i18n.changeLanguage(locale);
        dayjs.locale(locale);
    }, [locale]);

    useEffect(() => {
        if (auth.user?.access_token) {
            useAuthStore.getState().setAuthToken(auth.user?.access_token);
        }
    }, [auth.user?.access_token]);

    switch (auth.activeNavigator) {
        case "signinSilent":
            return <div>Signing you in...</div>;
        case "signoutRedirect":
            return <div>Signing you out...</div>;
    }

    if (auth.isLoading) {
        return <div>Loading...</div>;
    }

    if (!auth.isAuthenticated) {
        if (!useAuthStore.getState().refreshAttempt) {
            useAuthStore.setState({ refreshAttempt: true, isLoading: true });
            auth.signinRedirect();
            return <div>Refreshing...</div>;
        }

        // Store the current URL if we're not already on the auth page
        if (location.pathname !== Routes.Auth) {
            const redirectUrl = location.pathname + location.search;
            useAuthStore.getState().setRedirectUrl(redirectUrl);
        }

        if (useAuthStore.getState().isLoading) {
            return <div>Loading...</div>;
        }

        return <Navigate to={Routes.Auth} />;
    } else {
        useAuthStore.setState({ refreshAttempt: false, isLoading: false });
    }

    return <Outlet />;
}
